/**
 * Fretboard State Collector - Extracts comprehensive visual state data from the guitar neck
 * Following the specification in docs/plans/logServiceFretboard.md
 */

import {
  getGuitarTuning,
  getNoteAtFret,
  isModeColorNote,
  SINGLE_DOT_FRETS,
  DOUBLE_DOT_FRETS
} from '../components/GuitarNeckVisualizer.utils';
import { getProperNoteDisplay } from './musicTheory';
import { getNoteClassNames } from '../styles/NoteStyles';

/**
 * Collects comprehensive fretboard visual state for debugging
 * @param {Object} guitarNeckProps - Props passed to GuitarNeckVisualizer
 * @param {Object} calculatedState - Calculated state values from the visualizer
 * @returns {Object} Complete fretboard state data
 */
export const collectFretboardState = (guitarNeckProps, calculatedState) => {
  const {
    activeScale,
    selectedNotes = [],
    highlightArpeggios = false,
    selectedArpeggio = null,
    highlightChordNotes = false,
    visibleStrings = null,
    startFret = 1,
    numFretsToShow = 23,
    currentColor = 'orange',
    visibleColors = ['orange', 'red', 'green', 'blue'],
    highlightedFrets = [],
    isNoteInPattern = () => false,
    isArpeggioNote = () => false,
    isChordNote = () => false,
    getArpeggioDegree = () => '',
    getChordDegree = () => ''
  } = guitarNeckProps;

  const fretboardData = [];
  const timestamp = new Date().toISOString();
  const GUITAR_TUNING = getGuitarTuning();

  // For each guitar string
  GUITAR_TUNING.forEach((stringName, stringIndex) => {
    // For each visible fret (including open string)
    for (let fretIndex = 0; fretIndex <= numFretsToShow; fretIndex++) {
      const actualFret = startFret + fretIndex;
      const noteValue = getNoteAtFret(stringName, actualFret);
      const displayNote = getProperNoteDisplay(noteValue, activeScale);
      
      // Calculate all the visual state properties
      const isInScale = activeScale && activeScale.notes ? activeScale.notes.includes(displayNote) : false;
      const isRootNote = activeScale && displayNote === activeScale.scaleRoot;
      const isModeColor = activeScale ? isModeColorNote(displayNote, activeScale) : false;
      const isInPattern = typeof isNoteInPattern === 'function' ? isNoteInPattern(stringIndex, actualFret) : false;
      
      // Find if this note is selected
      const selectedNote = selectedNotes.find(n => n.stringIndex === stringIndex && n.fret === actualFret);
      const isSelected = !!selectedNote;
      
      // Arpeggio calculations
      const isArpeggio = typeof isArpeggioNote === 'function' ? 
        isArpeggioNote(stringIndex, actualFret, activeScale, selectedArpeggio) : false;
      const arpeggioDegree = typeof getArpeggioDegree === 'function' ? 
        getArpeggioDegree(stringIndex, actualFret, activeScale, selectedArpeggio) : '';
      
      // Chord calculations
      const isChordTone = activeScale && Array.isArray(activeScale.chordTones) ? 
        activeScale.chordTones.includes(displayNote) : false;
      const isChordHighlight = typeof isChordNote === 'function' ? 
        isChordNote(stringIndex, actualFret, activeScale) : false;
      const chordDegree = typeof getChordDegree === 'function' ? 
        getChordDegree(stringIndex, actualFret, activeScale) : '';
      
      // Visual state calculations
      const selectedColor = selectedNote ? selectedNote.color : currentColor;
      const isColorVisible = visibleColors.includes(selectedColor);
      const isVisible = isInScale && (visibleStrings === null || visibleStrings.has(stringIndex));
      const noteOpacity = visibleStrings === null || visibleStrings.has(stringIndex) ? 1 : 0.1;
      
      // CSS classes and styling
      const cssClasses = isInScale ? getNoteClassNames({
        noteValue,
        isRoot: isRootNote,
        isModeColorNote: isModeColor,
        noteIsInPattern: isInPattern,
        isInScale,
        isSelected,
        isArpeggio,
        isChordTone,
        isChordHighlightNote: isChordHighlight,
        arpeggioDegree,
        selectedColor,
        visibleColors
      }) : '';
      
      // Fret markers
      const isMarkerFret = SINGLE_DOT_FRETS.includes(actualFret) || DOUBLE_DOT_FRETS.includes(actualFret);
      const isHighlightedFret = highlightedFrets.includes(actualFret);
      
      // Create the data row
      const fretData = {
        // Position and identification
        Position: `S${stringIndex}F${actualFret}-${noteValue}`,
        StringIndex: stringIndex,
        StringName: stringName,
        FretNumber: actualFret,
        NoteName: noteValue,
        DisplayNote: displayNote,
        
        // Visibility and scale relationship
        IsVisible: isVisible,
        IsInScale: isInScale,
        IsRootNote: isRootNote,
        IsModeColor: isModeColor,
        
        // Pattern and selection
        IsInPattern: isInPattern,
        IsSelected: isSelected,
        SelectedColor: selectedColor,
        IsColorVisible: isColorVisible,
        
        // Arpeggio highlighting
        IsArpeggio: isArpeggio,
        ArpeggioDegree: arpeggioDegree,
        
        // Chord highlighting
        IsChordTone: isChordTone,
        IsChordHighlight: isChordHighlight,
        ChordDegree: chordDegree,
        
        // Visual styling
        CSSClasses: cssClasses,
        Opacity: noteOpacity,
        BackgroundColor: calculateBackgroundColor({
          isSelected,
          isChordHighlight,
          isArpeggio,
          isRootNote,
          isModeColor,
          selectedColor,
          isInPattern
        }),
        BorderStyle: calculateBorderStyle({
          isSelected,
          isChordHighlight,
          isArpeggio,
          isRootNote,
          isModeColor
        }),
        TextColor: calculateTextColor({
          isSelected,
          isChordHighlight,
          isArpeggio,
          isRootNote,
          isModeColor
        }),
        ZIndex: calculateZIndex({
          isSelected,
          isChordHighlight,
          isArpeggio,
          isInScale
        }),
        
        // Fret information
        IsMarkerFret: isMarkerFret,
        IsHighlightedFret: isHighlightedFret
      };
      
      fretboardData.push(fretData);
    }
  });

  // Context data
  const contextData = {
    ActiveScale: activeScale ? {
      scaleRoot: activeScale.scaleRoot,
      modeName: activeScale.modeName,
      notes: activeScale.notes,
      chordTones: activeScale.chordTones
    } : null,
    SelectedArpeggio: selectedArpeggio,
    CurrentProgression: calculatedState?.currentProgression || null,
    SelectedChord: calculatedState?.activeChordNote || null,
    DisplayedChord: calculatedState?.displayedChordNote || null,
    StartFret: startFret,
    NumFretsToShow: numFretsToShow,
    ViewportWidth: typeof window !== 'undefined' ? window.innerWidth : null,
    IsMobile: typeof window !== 'undefined' && window.innerWidth <= 768,
    IsLandscape: typeof window !== 'undefined' && window.innerWidth > window.innerHeight,
    HighlightArpeggios: highlightArpeggios,
    HighlightChordNotes: highlightChordNotes,
    VisibleColors: visibleColors,
    CurrentColor: currentColor
  };

  return {
    fretboardData,
    contextData,
    timestamp,
    totalNotes: fretboardData.length,
    visibleNotes: fretboardData.filter(n => n.IsVisible).length,
    selectedNotes: fretboardData.filter(n => n.IsSelected).length,
    inScaleNotes: fretboardData.filter(n => n.IsInScale).length
  };
};

/**
 * Calculate background color based on note properties
 */
function calculateBackgroundColor({ isSelected, isChordHighlight, isArpeggio, isRootNote, isModeColor, selectedColor, isInPattern }) {
  if (isSelected) {
    // Color mapping for selected notes
    const colorMap = {
      orange: '#FF9800',
      red: '#F44336',
      green: '#4ade80',
      blue: '#60a5fa'
    };
    return colorMap[selectedColor] || '#FF9800';
  }

  if (isChordHighlight) return '#c084fc'; // Modern muted purple for chord highlights
  if (isArpeggio) return '#FF5722'; // Deep orange for arpeggios
  if (isRootNote) return '#FFC107'; // Amber for root notes
  if (isModeColor) return '#795548'; // Brown for mode color notes
  
  return '#000000'; // Black default
}

/**
 * Calculate border style based on note properties
 */
function calculateBorderStyle({ isSelected, isChordHighlight, isArpeggio, isRootNote, isModeColor }) {
  if (isSelected) return '2px solid #ffffff';
  if (isChordHighlight) return '2px solid #E91E63';
  if (isArpeggio) return '2px solid #FF9800';
  if (isRootNote) return '2px solid #FFEB3B';
  if (isModeColor) return '2px solid #8BC34A';
  
  return '1px solid rgba(255, 255, 255, 0.3)';
}

/**
 * Calculate text color based on note properties
 */
function calculateTextColor({ isSelected, isChordHighlight, isArpeggio, isRootNote, isModeColor }) {
  if (isSelected || isChordHighlight || isArpeggio) return '#ffffff';
  if (isRootNote || isModeColor) return '#000000';
  
  return '#ffffff';
}

/**
 * Calculate z-index based on note properties
 */
function calculateZIndex({ isSelected, isChordHighlight, isArpeggio, isInScale }) {
  if (isSelected) return 10;
  if (isChordHighlight) return 8;
  if (isArpeggio) return 6;
  if (isInScale) return 3;
  
  return 1;
}

export default collectFretboardState;